import { useState, type ReactNode } from 'react'
import { Icon } from '../ui/Icon'
import { CAPS, type Cap, type El, type Op, type Props, type Route } from './model'
import { CapMark, capTrim, capsOf } from './ElementView'

/**
 * A chosen line's controls, laid out as Miro lays them (owner, 2026-11-04: 你去 Miro 仔細研究他的 edge 是怎麼設計的. 然後把
 * 它原封不動的搬到我們的 app 裡面來 — studied on his board that day): the start's cap, a swap, the end's cap; then Type —
 * thickness, straight / elbowed / curved, solid / dashed / dotted, line jumps. The colour and the button that puts words
 * on the line follow, in the bar itself.
 */
type LineMenu = 'tail' | 'head' | 'type' | null

const CAP_NAMES: Record<Cap, string> = {
  none: 'None',
  stealth: 'Arrow',
  arrow: 'Filled triangle',
  open: 'Open arrow',
  triangle: 'Triangle',
  diamond_f: 'Filled diamond',
  diamond: 'Diamond',
  circle_f: 'Filled circle',
  circle: 'Circle',
  erd_one: 'One',
  erd_only_one: 'Only one',
  erd_zero_or_one: 'Zero or one',
  erd_many: 'Many',
  erd_one_or_many: 'One or many',
  erd_zero_or_many: 'Zero or many',
}

/** A cap as a little line ending in it, pointing the way that end does (left for the start, right for the end). */
function CapGlyph({ cap, end }: { cap: Cap; end: 'head' | 'tail' }) {
  if (cap === 'none') return <span className="bd-cap-none">None</span>
  const W = 30
  const s = 9
  const sw = 1.6
  const right = end === 'head'
  const tip = { x: right ? W - 3 : 3, y: 9 }
  const dir = { x: right ? 1 : -1, y: 0 }
  const back = capTrim(cap, s, sw)
  const from = right ? 3 : W - 3
  const to = tip.x - dir.x * back
  return (
    <svg width={W} height={18} viewBox={`0 0 ${W} 18`} aria-hidden="true" className="bd-cap-glyph">
      <path d={`M${from},9 L${to},9`} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" fill="none" />
      <CapMark cap={cap} at={tip} dir={dir} s={s} sw={sw} color="currentColor" paper="none" />
    </svg>
  )
}

function RouteGlyph({ route }: { route: Route }) {
  const d = route === 'straight' ? 'M3,15 L21,3' : route === 'curve' ? 'M3,13 C6,3 10,3 12,9 S18,15 21,5' : 'M3,15 L12,15 L12,3 L21,3'
  return (
    <svg width={24} height={18} viewBox="0 0 24 18" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth={1.8} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function StyleGlyph({ style }: { style: 'solid' | 'dashed' | 'dotted' }) {
  return (
    <svg width={28} height={10} viewBox="0 0 28 10" aria-hidden="true">
      <path
        d="M3,5 L25,5"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={style === 'dashed' ? '6 4' : style === 'dotted' ? '0.01 4.5' : undefined}
        fill="none"
      />
    </svg>
  )
}

const ROUTES: Route[] = ['straight', 'elbow', 'curve']
const ROUTE_NAMES: Record<Route, string> = { straight: 'Straight', elbow: 'Elbowed', curve: 'Curved' }
const STYLES = ['solid', 'dashed', 'dotted'] as const
const styleOf = (e: El) => (e.dash ? (e.dot ? 'dotted' : 'dashed') : 'solid')

/** The line widths the thickness slider stops at. */
const WIDTHS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 20]

export function LineControls({
  sel,
  onChange,
  popover,
}: {
  sel: El[]
  onChange: (ops: Op[]) => void
  /** The bar's popover (it closes on a press outside, and sits under the bar on a phone). */
  popover: (props: { className: string; onClose: () => void; children: ReactNode }) => ReactNode
}) {
  const [menu, setMenu] = useState<LineMenu>(null)
  const first = sel[0]!
  const { head, tail } = capsOf(first)
  const setAll = (props: Props) => onChange(sel.map((e) => ({ id: e.id, set: props })))
  const sw = first.sw ?? 2
  const route = first.route ?? 'elbow'
  const capMenu = (end: 'head' | 'tail') =>
    popover({
      className: 'bd-pop-ctx bd-pop-caps',
      onClose: () => setMenu(null),
      children: (
        <div className="bd-cap-grid" role="menu" aria-label={end === 'tail' ? 'Line start' : 'Line end'}>
          {CAPS.map((c) => (
            <button
              key={c}
              type="button"
              role="menuitemradio"
              aria-checked={(end === 'head' ? head : tail) === c}
              className={`bd-cap-btn${(end === 'head' ? head : tail) === c ? ' on' : ''}`}
              title={CAP_NAMES[c]}
              aria-label={CAP_NAMES[c]}
              onClick={() => {
                setAll({ [end]: c })
                setMenu(null)
              }}
            >
              <CapGlyph cap={c} end={end} />
            </button>
          ))}
        </div>
      ),
    })
  return (
    <>
      <div className="bd-ctx-group">
        <div className="bd-ctx-wrap">
          <button
            type="button"
            className={`bd-ctx-btn bd-ctx-cap${menu === 'tail' ? ' on' : ''}`}
            title="Line start"
            aria-label={`Line start: ${CAP_NAMES[tail]}`}
            aria-haspopup="menu"
            aria-expanded={menu === 'tail'}
            onClick={() => setMenu(menu === 'tail' ? null : 'tail')}
          >
            <CapGlyph cap={tail} end="tail" />
          </button>
          {menu === 'tail' && capMenu('tail')}
        </div>
        <button
          type="button"
          className="bd-ctx-btn"
          title="Swap line ends"
          aria-label="Swap line ends"
          // Each line swaps its own two caps: the arrow turns round, the line stays where it is (Miro's swap).
          onClick={() => onChange(sel.map((e) => ({ id: e.id, set: { head: capsOf(e).tail, tail: capsOf(e).head } })))}
        >
          <Icon name="swap_horiz" size={18} />
        </button>
        <div className="bd-ctx-wrap">
          <button
            type="button"
            className={`bd-ctx-btn bd-ctx-cap${menu === 'head' ? ' on' : ''}`}
            title="Line end"
            aria-label={`Line end: ${CAP_NAMES[head]}`}
            aria-haspopup="menu"
            aria-expanded={menu === 'head'}
            onClick={() => setMenu(menu === 'head' ? null : 'head')}
          >
            <CapGlyph cap={head} end="head" />
          </button>
          {menu === 'head' && capMenu('head')}
        </div>
      </div>
      <div className="bd-ctx-group">
        <div className="bd-ctx-wrap">
          <button
            type="button"
            className={`bd-ctx-btn bd-ctx-type${menu === 'type' ? ' on' : ''}`}
            title="Line type"
            aria-label="Line type"
            aria-haspopup="dialog"
            aria-expanded={menu === 'type'}
            onClick={() => setMenu(menu === 'type' ? null : 'type')}
          >
            <RouteGlyph route={route} />
            <span className="bd-ctx-caption">Type</span>
          </button>
          {menu === 'type' &&
            popover({
              className: 'bd-pop-ctx bd-pop-type',
              onClose: () => setMenu(null),
              children: (
                <div className="bd-type">
                  <label className="bd-thick">
                    <span className="bd-type-label">Thickness</span>
                    <input
                      type="range"
                      min={0}
                      max={WIDTHS.length - 1}
                      step={1}
                      value={Math.max(0, WIDTHS.findIndex((w) => w >= sw))}
                      aria-valuetext={`${sw} px`}
                      onChange={(e) => setAll({ sw: WIDTHS[Number(e.currentTarget.value)] ?? 2 })}
                    />
                    <span className="bd-thick-val">{sw}</span>
                  </label>
                  <div className="bd-type-row" role="group" aria-label="Line shape">
                    {ROUTES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        className={`bd-type-btn${route === r ? ' on' : ''}`}
                        title={ROUTE_NAMES[r]}
                        aria-label={ROUTE_NAMES[r]}
                        aria-pressed={route === r}
                        // A new shape starts from its own way again: bends made for one shape mean nothing to another.
                        onClick={() => setAll({ route: r, pts: [] })}
                      >
                        <RouteGlyph route={r} />
                      </button>
                    ))}
                  </div>
                  <div className="bd-type-row" role="group" aria-label="Line style">
                    {STYLES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`bd-type-btn${styleOf(first) === s ? ' on' : ''}`}
                        title={s[0]!.toUpperCase() + s.slice(1)}
                        aria-label={s[0]!.toUpperCase() + s.slice(1)}
                        aria-pressed={styleOf(first) === s}
                        onClick={() => setAll(s === 'solid' ? { dash: false, dot: false } : { dash: true, dot: s === 'dotted' })}
                      >
                        <StyleGlyph style={s} />
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={`bd-type-jumps${first.jump ? ' on' : ''}`}
                    aria-pressed={!!first.jump}
                    onClick={() => setAll({ jump: !first.jump })}
                  >
                    <svg width={24} height={14} viewBox="0 0 24 14" aria-hidden="true">
                      <path d="M2,11 L8,11 A4,4 0 0 1 16,11 L22,11" stroke="currentColor" strokeWidth={1.8} fill="none" strokeLinecap="round" />
                    </svg>
                    <span>Line jumps</span>
                  </button>
                </div>
              ),
            })}
        </div>
      </div>
    </>
  )
}
